import { motion } from 'framer-motion'
import { LucideIcon, CheckCircle, FileText, MessageSquare, Award, Clock } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'

interface ActivityItem {
    id: string
    type: 'test' | 'submission' | 'message' | 'achievement' | 'reminder'
    title: string
    description?: string
    timestamp: string
    link?: string
}

interface ActivityFeedProps {
    activities: ActivityItem[]
    maxItems?: number
}

const activityIcons: Record<string, LucideIcon> = {
    test: FileText,
    submission: CheckCircle,
    message: MessageSquare,
    achievement: Award,
    reminder: Clock,
}

const activityColors: Record<string, string> = {
    test: 'bg-blue-500/10 text-blue-500',
    submission: 'bg-green-500/10 text-green-500',
    message: 'bg-purple-500/10 text-purple-500',
    achievement: 'bg-amber-500/10 text-amber-500',
    reminder: 'bg-slate-500/10 text-slate-500',
}

export function ActivityFeed({ activities, maxItems = 5 }: ActivityFeedProps) {
    const displayedActivities = activities.slice(0, maxItems)

    return (
        <GlassmorphicCard className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-base md:text-lg font-bold text-theme-primary">Recent Activity</h3>
                <button className="text-xs md:text-sm text-primary-blue font-medium hover:underline">
                    View All
                </button>
            </div>

            <div className="space-y-3">
                {displayedActivities.length === 0 ? (
                    <p className="text-sm text-theme-secondary text-center py-4">No recent activity</p>
                ) : (
                    displayedActivities.map((activity, index) => {
                        const Icon = activityIcons[activity.type] || CheckCircle
                        const colorClass = activityColors[activity.type] || activityColors.submission

                        return (
                            <motion.div
                                key={activity.id}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/30 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                            >
                                <div className={`p-2 rounded-lg ${colorClass} shrink-0`}>
                                    <Icon className="h-4 w-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-theme-primary truncate">{activity.title}</p>
                                    {activity.description && (
                                        <p className="text-xs text-theme-secondary truncate">{activity.description}</p>
                                    )}
                                </div>
                                <span className="text-[10px] md:text-xs text-theme-muted whitespace-nowrap shrink-0">
                                    {activity.timestamp}
                                </span>
                            </motion.div>
                        )
                    })
                )}
            </div>
        </GlassmorphicCard>
    )
}
